import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useVidyoStore } from "@/features/vidyo-call/utils/vidyo-store";

export function JoinBreakoutSessionModal() {
  const isVidyoConnected = useVidyoStore((store) => store.isVidyoConnected);
  const [isJoinBreakoutSessionModalOpen, setIsJoinBreakoutSessionModalOpen] =
    useState(false);
  const [countdown, setCountdown] = useState(15);
  const navigate = useNavigate();

  const shouldJoinToBreakoutSession = useVidyoStore(
    (store) => store.breakoutSession.shouldJoinToBreakoutSession,
  );
  const assignedBreakoutRoomKey = useVidyoStore(
    (store) => store.breakoutSession.assignedBreakoutRoomKey,
  );
  const redirectReason = useVidyoStore(
    (store) => store.breakoutSession.redirectReason,
  );

  useEffect(() => {
    if (
      isVidyoConnected &&
      shouldJoinToBreakoutSession &&
      redirectReason !== "moved-to-breakout-session"
    ) {
      setIsJoinBreakoutSessionModalOpen(true);
    }
  }, [isVidyoConnected, shouldJoinToBreakoutSession, redirectReason]);

  const handleJoinBreakoutSession = useCallback(async () => {
    if (!assignedBreakoutRoomKey) {
      return;
    }

    setIsJoinBreakoutSessionModalOpen(false);

    navigate(`/r/${assignedBreakoutRoomKey}/call`, { replace: true });
  }, [navigate, assignedBreakoutRoomKey]);

  /**
   * If participants was drag and dropped to a breakout session,
   * we need to join the breakout session immediately.
   */
  useEffect(() => {
    if (
      redirectReason === "moved-to-breakout-session" &&
      shouldJoinToBreakoutSession
    ) {
      handleJoinBreakoutSession();
    }
  }, [redirectReason, handleJoinBreakoutSession, shouldJoinToBreakoutSession]);

  useEffect(() => {
    if (!isJoinBreakoutSessionModalOpen || !isVidyoConnected) {
      setCountdown(15);
      return;
    }

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleJoinBreakoutSession();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [
    isJoinBreakoutSessionModalOpen,
    handleJoinBreakoutSession,
    isVidyoConnected,
  ]);

  return (
    <AlertDialog
      open={isJoinBreakoutSessionModalOpen}
      onOpenChange={setIsJoinBreakoutSessionModalOpen}
    >
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>Join Breakout Session?</AlertDialogTitle>
          <AlertDialogDescription>
            You are about to join a breakout session. This will end your current
            session and join the breakout session. You can leave the breakout
            session at any time.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter className="sm:justify-end">
          <Button
            isAnimated
            onClick={handleJoinBreakoutSession}
            className="min-w-[135px]" // prevent layout shift
            variant="default"
          >
            Join Now{" "}
            <span className="ml-1 font-normal text-foreground/40">
              ({countdown}s)
            </span>
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
